/**
 * Organization Call to Action Hooks
 * React Query hooks for managing organization CTAs
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UUID } from "../types/common.types";
import type { Organization } from "../types/organization.types";
import type {
  CreateOrganizationCTAInput,
  UpdateOrganizationCTAInput,
} from "../types/organizationCTA.types";
import {
  getOrganizationCTAs,
  getOrganizationCTA,
  createOrganizationCTA,
  updateOrganizationCTA,
  deleteOrganizationCTA,
  generateOrganizationCTAQRCode,
  getOffersForOrganization,
} from "../services/organizationCTA.service";
import { useAuth } from "../contexts/AuthContext";

// Query keys
export const organizationCTAKeys = {
  all: ["organizationCTAs"] as const,
  list: (organizationId: UUID) =>
    [...organizationCTAKeys.all, "list", organizationId] as const,
  detail: (ctaId: UUID) =>
    [...organizationCTAKeys.all, "detail", ctaId] as const,
  offers: (organizationId: UUID) =>
    [...organizationCTAKeys.all, "offers", organizationId] as const,
};

/**
 * Hook to get all CTAs for an organization
 */
export function useOrganizationCTAs(organizationId: UUID | undefined) {
  return useQuery({
    queryKey: organizationCTAKeys.list(organizationId!),
    queryFn: () => getOrganizationCTAs(organizationId!),
    enabled: !!organizationId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to get a single CTA
 */
export function useOrganizationCTA(ctaId: UUID | undefined) {
  return useQuery({
    queryKey: organizationCTAKeys.detail(ctaId!),
    queryFn: () => getOrganizationCTA(ctaId!),
    enabled: !!ctaId,
    staleTime: 30000,
  });
}

/**
 * Hook to get available offers for an organization
 */
export function useOffersForOrganization(organizationId: UUID | undefined) {
  return useQuery({
    queryKey: organizationCTAKeys.offers(organizationId!),
    queryFn: () => getOffersForOrganization(organizationId!),
    enabled: !!organizationId,
    staleTime: 60000, // 1 minute - offers change less frequently
  });
}

/**
 * Hook to create a new CTA
 */
export function useCreateOrganizationCTA() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (input: CreateOrganizationCTAInput) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      return createOrganizationCTA(input, user.id);
    },
    onSuccess: (data) => {
      // Invalidate CTAs list for this organization
      queryClient.invalidateQueries({
        queryKey: organizationCTAKeys.list(data.organization_id),
      });
    },
  });
}

/**
 * Hook to update an existing CTA
 */
export function useUpdateOrganizationCTA() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: ({
      ctaId,
      updates,
    }: {
      ctaId: UUID;
      updates: UpdateOrganizationCTAInput;
    }) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      return updateOrganizationCTA(ctaId, updates, user.id);
    },
    onSuccess: (data) => {
      // Invalidate CTAs list for this organization
      queryClient.invalidateQueries({
        queryKey: organizationCTAKeys.list(data.organization_id),
      });
      // Invalidate this specific CTA
      queryClient.invalidateQueries({
        queryKey: organizationCTAKeys.detail(data.id),
      });
    },
  });
}

/**
 * Hook to delete a CTA
 */
export function useDeleteOrganizationCTA() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ctaId }: { ctaId: UUID; organizationId: UUID }) =>
      deleteOrganizationCTA(ctaId),
    onSuccess: (_, variables) => {
      // Invalidate CTAs list for this organization
      queryClient.invalidateQueries({
        queryKey: organizationCTAKeys.list(variables.organizationId),
      });
    },
  });
}

/**
 * Hook to generate/regenerate QR code for a CTA
 */
export function useGenerateOrganizationCTAQRCode() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: ({
      ctaId,
      organization,
    }: {
      ctaId: UUID;
      organization: Organization;
    }) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      return generateOrganizationCTAQRCode(ctaId, organization, user.id);
    },
    onSuccess: (data) => {
      // Invalidate CTAs list for this organization
      queryClient.invalidateQueries({
        queryKey: organizationCTAKeys.list(data.organization_id),
      });
      // Invalidate this specific CTA
      queryClient.invalidateQueries({
        queryKey: organizationCTAKeys.detail(data.id),
      });
    },
  });
}
